/**
 * Generic TanStack Table expansion helpers
 *
 * Key principle:
 * - We store expanded state as a boolean map: { [rowId]: true | false }
 * - This allows us to persist a user "collapsed" override as `false`
 *   (instead of deleting the key, which gets re-expanded by defaultExpanded merges).
 */

import type { ExpandedState, Row, Table } from "@tanstack/react-table";

/**
 * Convert ExpandedState to a Record<string, boolean> safely.
 * 
 * TanStack's ExpandedState can be `true` (all expanded) or a map.
 * For our persisted UI we treat `true` as "no explicit overrides".
 */
export function asExpandedRecord(expanded: ExpandedState | undefined): Record<string, boolean> {
  if (!expanded) return {};
  if (expanded === true) return {};
  if (typeof expanded === "object" && expanded !== null && !Array.isArray(expanded)) {
    return expanded as Record<string, boolean>;
  }
  return {};
}

export function setRowExpanded<T>(
  table: Table<T>,
  row: Row<T>,
  nextExpanded: boolean,
): void {
  const current = asExpandedRecord(table.getState().expanded);
  // Only create new object if value actually changes (optimization)
  if (current[row.id] === nextExpanded) return;
  
  const next: Record<string, boolean> = { ...current, [row.id]: nextExpanded };
  table.options.onExpandedChange?.(next as ExpandedState);
}

export function toggleRowExpanded<T>(table: Table<T>, row: Row<T>): void {
  const isExpanded = row.getIsExpanded();
  setRowExpanded(table, row, !isExpanded);
}

export function setRowsExpanded<T>(
  table: Table<T>,
  rowIds: string[],
  nextExpanded: boolean,
): void {
  if (!rowIds.length) return;
  const current = asExpandedRecord(table.getState().expanded);
  const next: Record<string, boolean> = { ...current };
  for (const id of rowIds) next[id] = nextExpanded;
  table.options.onExpandedChange?.(next as ExpandedState);
}

